import { IUser } from "../../contexts/AuthContext";
import { sendToken, updateUsuario } from "../../services/api";
import {
  Input,
  Modal,
  ModalBody,
  ModalOverlay,
  ModalContent,
  Button,
  Stack,
  FormControl,
  FormLabel,
  Avatar,
  Heading,
  AvatarBadge,
  IconButton,
  Flex,
  useToast,
  Link,
} from "@chakra-ui/react";
import { FormEvent, useEffect, useRef, useState } from "react";
import { FaPencilAlt } from "react-icons/fa";
import { CampoForm } from "../CampoForm";
import { ErrosForm, useErrosForm } from "../../hooks/useErrosForm";
import { emailInvalido, vazio } from "../../utils/validacao";
import { Loading } from "../Loading";
import useAuth from "../../hooks/useAuth";
import { toBase64, uploadWithBase64 } from "../../utils/imageHelper";
import { useNavigate } from "react-router-dom";

type ModalPerfilProps = {
  onClose: () => void;
  isOpen: boolean;
  onOpen: () => void;
  user: IUser | undefined;
};

type Campo = "nome" | "sobrenome" | "email";

export const ModalPerfil = ({ isOpen, onClose, user }: ModalPerfilProps) => {
  const [image64, setImage64] = useState('');
  const [loading, setLoading] = useState(false);
  const [updateImage, setUpdateImage] = useState<boolean>(false);

  const [avatarUrl, setAvatarUrl] = useState('');
  const [nome, setNome] = useState('');
  const [sobrenome, setSobrenome] = useState('');
  const [email, setEmail] = useState('');

  const { setUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const inputFile = useRef<any>();
  const { erros, validar, limparErro, limparTudo } = useErrosForm<Campo>();

  useEffect(() => {
    if(isOpen){
      setImage64('');
      setUpdateImage(false);
      limparTudo();
    }
  }, [isOpen, limparTudo]);

  useEffect(() => {
    if(user){
      const partes = user.name.split(" ");
      setAvatarUrl(user.avatar);
      setNome(partes[0]);
      // slice em vez de [1]: nomes com mais de duas partes perdiam o resto, e
      // nomes de uma palavra so gravavam undefined no estado.
      setSobrenome(partes.slice(1).join(" "));
      setEmail(user.username);
    }
  }, [user]);

  const handleChangeAvatar = () => {
    inputFile.current.click();
  };

  const handleSelectedFile = async (files: FileList | null) => {
    if (files && files[0] && files[0].size < 10000000) {
      const picBase64 = await toBase64(files[0]);
      setImage64(`${picBase64}`);
      setUpdateImage(true);
    } else {
      toast({
        title: `Foto muito pesada, selecione outra!`,
        status: "warning",
        isClosable: true,
      });
    }
  };

  const handleToken = () => {
    sendToken(user?.username+'')
      .then(() => {
        navigate('/change-password/' + user?.username);
      })
      .catch(() => {
        toast({
          title: `Não foi possível enviar o código.`,
          status: 'error',
          isClosable: true
        });
      });
  };

  const montarErros = (): ErrosForm<Campo> => {
    const encontrados: ErrosForm<Campo> = {};

    if (vazio(nome)) encontrados.nome = "Nome é obrigatório!";
    if (vazio(sobrenome)) encontrados.sobrenome = "Sobrenome é obrigatório!";

    if (vazio(email)) {
      encontrados.email = "Email é obrigatório!";
    } else if (emailInvalido(email)) {
      encontrados.email = "Informe um email válido.";
    }

    return encontrados;
  };

  // Antes nao havia validacao nenhuma: dava para salvar o perfil com nome,
  // sobrenome e email em branco.
  const handleUpdateUsuario = (event: FormEvent) => {
    event.preventDefault();
    if (!validar(montarErros())) return;

    const nomeLimpo = nome.trim();
    const sobrenomeLimpo = sobrenome.trim();
    const emailLimpo = email.trim();

    setLoading(true);
    // Sem troca de foto reaproveitamos a url atual, em vez de subir de novo.
    const avatarPronto = updateImage ? uploadWithBase64(image64) : Promise.resolve(avatarUrl);

    avatarPronto
      .then((link) =>
        updateUsuario(`${user?.username}`, {
          avatarUrl: link,
          email: emailLimpo,
          nome: nomeLimpo,
          sobrenome: sobrenomeLimpo,
        }).then(() => link)
      )
      .then((link) => {
        setUser({
          avatar: link,
          name: `${nomeLimpo} ${sobrenomeLimpo}`,
          username: emailLimpo,
          userType: user?.userType,
          sub: user?.sub,
        });
        setAvatarUrl(link);
        setLoading(false);
        setUpdateImage(false);
        onClose();
        toast({
          title: `Perfil editado com sucesso.`,
          status: "success",
          isClosable: true,
        });
        if (inputFile.current) inputFile.current.value = null;
      })
      // Sem este catch uma falha de upload deixava o spinner girando para sempre.
      .catch((error) => {
        setLoading(false);
        toast({
          title:
            error?.response?.data?.message ??
            `Não foi possível editar seu perfil.`,
          status: "error",
          isClosable: true,
        });
      });
  };

  return (
    <Modal onClose={onClose} isOpen={isOpen} isCentered>
      <ModalOverlay />
        <ModalContent>
          <ModalBody>
            <Heading
              lineHeight={1.1}
              fontSize={{ base: "2xl", sm: "3xl" }}
              marginBottom={"20px"}
            >
              Meu Perfil
            </Heading>
            {loading ? (
              <Loading label="Salvando..." />
            ) : (
              <form onSubmit={handleUpdateUsuario} noValidate>
                <FormControl id="avatar">
                  <FormLabel>Avatar</FormLabel>
                  <Flex marginBottom={"20px"}>
                    <Avatar size="xl" src={image64 ? image64 : user?.avatar }>
                      <AvatarBadge
                        as={IconButton}
                        size="sm"
                        rounded="full"
                        top="-10px"
                        colorScheme="red"
                        aria-label="Trocar foto"
                        icon={<FaPencilAlt />}
                        onClick={handleChangeAvatar}
                      />
                    </Avatar>
                    <Input
                      display="none"
                      type="file"
                      ref={inputFile}
                      onChange={(files) =>
                        handleSelectedFile(files.target.files)
                      }
                      accept="image/*"
                      multiple={false}
                    />
                  </Flex>
                </FormControl>

                <Flex gap={"15px"}>
                  <CampoForm label="Nome" htmlFor="nome" erro={erros.nome}>
                    <Input
                      id="nome"
                      placeholder="Nome"
                      _placeholder={{ color: "gray.500" }}
                      type="text"
                      autoComplete="given-name"
                      value={nome}
                      onChange={(e) => {
                        setNome(e.target.value);
                        limparErro("nome");
                      }}
                    />
                  </CampoForm>
                  <CampoForm label="Sobrenome" htmlFor="sobrenome" erro={erros.sobrenome}>
                    <Input
                      id="sobrenome"
                      placeholder="Sobrenome"
                      _placeholder={{ color: "gray.500" }}
                      type="text"
                      autoComplete="family-name"
                      value={sobrenome}
                      onChange={(e) => {
                        setSobrenome(e.target.value);
                        limparErro("sobrenome");
                      }}
                    />
                  </CampoForm>
                </Flex>

                <Flex marginTop="15px" marginBottom="10px">
                  <CampoForm label="Email" htmlFor="email" erro={erros.email}>
                    <Input
                      id="email"
                      placeholder="seu-email@exemplo.com"
                      _placeholder={{ color: "gray.500" }}
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        limparErro("email");
                      }}
                    />
                  </CampoForm>
                </Flex>

                <Link onClick={handleToken}>Trocar minha senha.</Link>
                <Stack
                  spacing={6}
                  direction={["column", "row"]}
                  marginTop={"20px"}
                  marginBottom={"20px"}
                >
                  <Button
                    type="button"
                    colorScheme="red"
                    variant="outline"
                    w="full"
                    onClick={onClose}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" colorScheme="blue" w="full">
                    Salvar
                  </Button>
                </Stack>
              </form>
            )}
          </ModalBody>
        </ModalContent>
    </Modal>
  );
};
